import { Eye, ChevronDown, Trash2, Edit, Plus } from "lucide-react";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatDateShort = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const getStatusBadge = (status) => {
  const statusClasses = {
    pending: "bg-yellow-100 text-yellow-800",
    contacted: "bg-blue-100 text-blue-800",
    enrolled: "bg-green-100 text-green-800",
    completed: "bg-warm-100 text-warm-800",
    unread: "bg-red-100 text-red-800",
    read: "bg-gray-100 text-gray-800",
    resolved: "bg-green-100 text-green-800",
    upcoming: "bg-blue-100 text-blue-800",
    in_progress: "bg-yellow-100 text-yellow-800",
    cancelled: "bg-red-100 text-red-800"
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClasses[status] || "bg-gray-100 text-gray-800"}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export const ReferralsTable = ({ referrals, onView, onUpdateStatus, onDelete }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Organization</TableHead>
          <TableHead>Client Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {referrals.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
              No referrals yet
            </TableCell>
          </TableRow>
        ) : (
          referrals.map((referral) => (
            <TableRow key={referral.id} data-testid={`referral-row-${referral.id}`}>
              <TableCell className="text-sm">{formatDate(referral.created_at)}</TableCell>
              <TableCell className="font-medium">{referral.name}</TableCell>
              <TableCell>{referral.organization}</TableCell>
              <TableCell>{referral.client_type}</TableCell>
              <TableCell>{getStatusBadge(referral.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onView(referral)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><ChevronDown className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => onUpdateStatus("referrals", referral.id, "pending")}>Mark Pending</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("referrals", referral.id, "contacted")}>Mark Contacted</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("referrals", referral.id, "enrolled")}>Mark Enrolled</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("referrals", referral.id, "completed")}>Mark Completed</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete("referrals", referral.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

export const SignupsTable = ({ signups, onView, onUpdateStatus, onDelete }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Schedule</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {signups.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
              No signups yet
            </TableCell>
          </TableRow>
        ) : (
          signups.map((signup) => (
            <TableRow key={signup.id} data-testid={`signup-row-${signup.id}`}>
              <TableCell className="text-sm">{formatDate(signup.created_at)}</TableCell>
              <TableCell className="font-medium">{signup.name}</TableCell>
              <TableCell>{signup.email}</TableCell>
              <TableCell className="max-w-[200px] truncate">{signup.preferred_schedule}</TableCell>
              <TableCell>{getStatusBadge(signup.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onView(signup)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><ChevronDown className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-signups", signup.id, "pending")}>Mark Pending</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-signups", signup.id, "contacted")}>Mark Contacted</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-signups", signup.id, "enrolled")}>Mark Enrolled</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-signups", signup.id, "completed")}>Mark Completed</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete("class-signups", signup.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

export const SessionsTable = ({ sessions, onEdit, onUpdateStatus, onDelete }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Schedule</TableHead>
          <TableHead>Time</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Enrolled</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
              No sessions scheduled. Click "Add Session" to create one.
            </TableCell>
          </TableRow>
        ) : (
          sessions.map((session) => (
            <TableRow key={session.id} data-testid={`session-row-${session.id}`}>
              <TableCell className="font-medium">{session.title}</TableCell>
              <TableCell className="text-sm">
                {session.day_of_week}s<br />
                <span className="text-gray-500">
                  {formatDateShort(session.start_date)} - {formatDateShort(session.end_date)}
                </span>
              </TableCell>
              <TableCell>{session.time}</TableCell>
              <TableCell>{session.location}</TableCell>
              <TableCell>{session.current_enrollment} / {session.max_capacity}</TableCell>
              <TableCell>{getStatusBadge(session.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(session)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><ChevronDown className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-sessions", session.id, "upcoming")}>Mark Upcoming</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-sessions", session.id, "in_progress")}>Mark In Progress</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-sessions", session.id, "completed")}>Mark Completed</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("class-sessions", session.id, "cancelled")}>Mark Cancelled</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete("class-sessions", session.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

export const ContactsTable = ({ contacts, onView, onUpdateStatus, onDelete }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
              No messages yet
            </TableCell>
          </TableRow>
        ) : (
          contacts.map((contact) => (
            <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
              <TableCell className="text-sm">{formatDate(contact.created_at)}</TableCell>
              <TableCell className="font-medium">{contact.name}</TableCell>
              <TableCell>{contact.subject}</TableCell>
              <TableCell>{contact.inquiry_type}</TableCell>
              <TableCell>{getStatusBadge(contact.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onView(contact)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><ChevronDown className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => onUpdateStatus("contacts", contact.id, "unread")}>Mark Unread</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("contacts", contact.id, "read")}>Mark Read</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onUpdateStatus("contacts", contact.id, "resolved")}>Mark Resolved</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete("contacts", contact.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);
